import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, AlertCircle, CheckCircle, MapPin } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { PageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/enquiry`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20 bg-neutral-950">
      <PageSEO page="contact" />
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1632670535530-aaf6e90042ca?w=1920&q=80"
            alt="Film production equipment - contact LBR Distribution UK film distributors"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4">
              GET IN TOUCH
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              CONTACT US
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Have a question about our services? Want to learn more about distribution or sales representation? Get in touch with our team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-display text-3xl tracking-wider text-white uppercase mb-8">SEND AN ENQUIRY</h2>

              {success ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-500/10 border border-green-500/30 p-8 text-center"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-white uppercase tracking-wider mb-2">MESSAGE SENT</h3>
                  <p className="text-neutral-400">
                    Thank you for your enquiry. Our team will get back to you within 48 hours.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    data-testid="send-another-btn"
                    className="mt-6 text-orange-500 hover:text-orange-400 font-medium"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      <span className="text-red-400">{error}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-white">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      data-testid="input-subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                      placeholder="What is your enquiry about?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                      placeholder="Tell us how we can help..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-form-btn"
                    className="w-full flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-500 disabled:bg-orange-600/50 disabled:cursor-not-allowed text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        SEND MESSAGE
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="sticky top-32">
                <h2 className="font-display text-3xl tracking-wider text-white uppercase mb-8">CONTACT INFO</h2>
                
                <div className="space-y-6 mb-12">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-600/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Our Address</h3>
                      <p className="text-neutral-400">
                        LBR Film Distribution<br />
                        First Floor, City Centre<br />
                        Swan Buildings, 20 Swan St<br />
                        Greater Manchester<br />
                        M4 5JW
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Film CTA */}
                <div className="bg-orange-500/10 border border-orange-500/30 p-6 mb-8">
                  <h3 className="text-white font-display text-xl uppercase tracking-wider mb-3">WANT TO SUBMIT A FILM?</h3>
                  <p className="text-neutral-400 text-sm mb-4">
                    If you have a completed film you'd like us to consider for distribution, please use our dedicated submission form.
                  </p>
                  <a
                    href="/submit"
                    data-testid="submit-film-link"
                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 px-6 uppercase tracking-widest text-sm transition-all"
                  >
                    SUBMIT YOUR FILM
                  </a>
                </div>

                {/* Legal Info */}
                <div className="bg-neutral-900/50 border border-white/10 p-6 mb-8">
                  <p className="text-neutral-500 text-sm leading-relaxed">
                    LBR Film Distribution. Registered in England and Wales.
                  </p>
                </div>

                {/* Internal Links */}
                <div className="bg-neutral-900/30 border border-white/10 p-6">
                  <h3 className="text-white font-display text-lg uppercase tracking-wider mb-4">EXPLORE OUR SERVICES</h3>
                  <div className="space-y-2">
                    <Link to="/distribution" className="block text-neutral-400 hover:text-orange-500 transition-colors text-sm">
                      VOD Distribution Services →
                    </Link>
                    <Link to="/sales" className="block text-neutral-400 hover:text-orange-500 transition-colors text-sm">
                      Film Sales Representation →
                    </Link>
                    <Link to="/marketing" className="block text-neutral-400 hover:text-orange-500 transition-colors text-sm">
                      Marketing Agency →
                    </Link>
                    <Link to="/faq" className="block text-neutral-400 hover:text-orange-500 transition-colors text-sm">
                      Frequently Asked Questions →
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
